import React from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faKey } from '@fortawesome/pro-light-svg-icons/faKey';
import { TypedMemo } from '../../../common/types';
import { ApiAccessToken } from './useApiAccessTokens';
import { ApiAccessTokenRow } from './ApiAccessTokenRow';
import { withBrandWord } from '../../../music24/components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the API tab; each part uses its own slice of it */
import styles from './apiAccess.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ApiAccessTokenTableProps {
  tokens: ApiAccessToken[];
  onRevoke: (id: number) => void;
  revokingId: number | null;
  isRevoking: boolean;
}

const ApiAccessTokenTableComponent: React.FC<ApiAccessTokenTableProps> = ({
  tokens,
  onRevoke,
  revokingId,
  isRevoking,
}) => {
  const intl = useIntl();

  if (tokens.length === 0) {
    return (
      <div className={styles.tokens}>
        <div className={styles.empty}>
          <div className={styles.emptyIcon}>
            <FontAwesomeIcon icon={faKey} />
          </div>
          <b className={styles.emptyTitle}>
            {intl.formatMessage({
              id: 'app.sml.settings.apiAccess.list.empty',
              defaultMessage: 'You have no active API tokens.',
            })}
          </b>
          <p className={styles.emptyBody}>
            {withBrandWord(
              intl.formatMessage({
                id: 'app.sml.settings.apiAccess.list.emptyBody',
                defaultMessage: 'Create one to start calling the MUSIC24 API from your own apps and scripts.',
              })
            )}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.tokens}>
      <div className={`${styles.row} ${styles.rowHead}`}>
        <span>{intl.formatMessage({ id: 'app.sml.settings.apiAccess.list.columnName', defaultMessage: 'Name' })}</span>
        <span>
          {intl.formatMessage({ id: 'app.sml.settings.apiAccess.list.columnToken', defaultMessage: 'Token' })}
        </span>
        <span className={styles.columnCreated}>
          {intl.formatMessage({ id: 'app.sml.settings.apiAccess.list.columnCreated', defaultMessage: 'Created' })}
        </span>
        <span>
          {intl.formatMessage({ id: 'app.sml.settings.apiAccess.list.columnLastUsed', defaultMessage: 'Last used' })}
        </span>
        <span aria-hidden="true" />
      </div>
      {tokens.map((token) => (
        <ApiAccessTokenRow
          key={token.id}
          token={token}
          onRevoke={onRevoke}
          isRevoking={isRevoking && revokingId === token.id}
        />
      ))}
    </div>
  );
};

export const ApiAccessTokenTable = TypedMemo(ApiAccessTokenTableComponent);
